<script setup lang="ts">
    import { computed, toRef } from 'vue'
    import type { PropType } from 'vue'
    import type { DependentEntityInfo } from "@common/types"
    import type { WeightsSetDeleterViewModel } from "@Criteria/Weights/Components/WeightsSetDeleter"

    // ---------------- props / emits ----------------

    const props = defineProps({
        viewModel: {
            type: Object as PropType<WeightsSetDeleterViewModel>,
            required: true
        }
    })

    const emit = defineEmits<{
        (e: 'delete-weights-set', weightsSetId: number): void
    }>()

    // ---------------- state ----------------

    const vm = toRef(props, 'viewModel')

    // ---------------- computed ----------------

    const opened = computed(() => vm.value.isOpen)
    const initialized = computed(() => vm.value.initialized)

    const weightsSetName = computed(() => vm.value.weightsSetName)
    const hasDependants = computed(() => vm.value.hasDependants)
    const dependenciesCount = computed(() => vm.value.dependenciesCount)
    const weightedScores = computed<DependentEntityInfo[]>(() => vm.value.weightedScores)

    // ---------------- actions ----------------

    function emitDeleteWeightsSet()
    {
        emit('delete-weights-set', vm.value.weightsSetId!)
    }

    function close()
    {
        vm.value.close()
    }
</script>

<template>
    <div class="modal-mask" v-if="opened">
        <div class="modal-wrapper">
            <div v-if="!initialized" class="modal-dialog">
                <div class="modal-content">
                    <div class="modal-header">
                        <h5 class="modal-title">Delete weights set</h5>
                        <button type="button" class="close" @click="close">&times;</button>
                    </div>
                    <div class="modal-body">
                        <h4><i class="fa fa-spinner fa-spin"></i> Checking dependencies...</h4>
                    </div>
                    <div class="modal-footer">
                        <button type="button" @click="close" class="btn btn-outline-secondary">Cancel</button>
                    </div>
                </div>
            </div>

            <div v-if="initialized && hasDependants" class="modal-dialog modal-lg modal-dialog-scrollable">
                <div class="modal-content">
                    <div class="modal-header">
                        <h5 class="modal-title">Delete weights set</h5>
                        <button type="button" class="close" @click="close">&times;</button>
                    </div>

                    <div class="modal-body">
                        <div class="alert alert-danger text-dark" role="alert">
                            <h5 class="alert-heading">Cannot delete this weights set</h5>
                            <p>
                                The weights set '<strong>{{weightsSetName}}</strong>' is used in <strong>{{dependenciesCount}}</strong> places.
                                To delete this weights set, first remove it from where it is used.
                            </p>
                        </div>
                        <div>
                            <div class="card" v-if="weightedScores.length > 0">
                                <div class="card-header">
                                    Weighted scores ({{weightedScores.length}})
                                </div>
                                <ul class="list-group list-group-flush">
                                    <li class="list-group-item" v-for="ws in weightedScores" :key="ws.Id">
                                        <a class="text-primary" v-sanitizedHref="ws.Url">{{ws.Name}}</a>
                                    </li>
                                </ul>
                            </div>
                        </div>
                    </div>

                    <div class="modal-footer">
                        <button type="button" @click="close" class="btn btn-outline-secondary">Close</button>
                    </div>
                </div>
            </div>

            <div v-if="initialized && !hasDependants" class="modal-dialog">
                <div class="modal-content">
                    <div class="modal-header bg-danger text-white">
                        <h5 class="modal-title">Delete weights set</h5>
                        <button type="button" class="close text-white" @click="close">&times;</button>
                    </div>

                    <div class="modal-body">
                        <h4>Are you sure you want to delete this weights set?</h4>
                        <p>You are about to delete '<strong>{{weightsSetName}}</strong>'. This can't be undone.</p>
                    </div>

                    <div class="modal-footer">
                        <button type="button" @click="emitDeleteWeightsSet" class="btn btn-danger">Delete</button>
                        <button type="button" @click="close" class="btn btn-outline-secondary">Cancel</button>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>